// ============================================
// Path: /frontend/src/pages/admin/PaymentsPage.jsx
// Purpose: Admin page for payments and monetization management
// UPDATED: Full featured payment history with sorting, filtering, pagination
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { adminAPI } from '../../api/admin.api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { CreditCard, History, SlidersHorizontal, List, Briefcase, FileText, Loader2, ChevronDown, ChevronUp, ArrowUpDown, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import IncomePieChart from './IncomePieChart';

// ========================================
// MAIN COMPONENT
// ========================================
export default function PaymentsPage() {
  console.log('🎨 [PaymentsPage] Component mounted');

  // ========================================
  // STATE
  // ========================================
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [isTableVisible, setIsTableVisible] = useState(false);
  const [allCompletedPayments, setAllCompletedPayments] = useState([]);

  // Filters and sorting
  const [filters, setFilters] = useState({
    status: '',
    plan: '',
    role: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // ========================================
  // FETCH PAYMENT HISTORY
  // ========================================
  useEffect(() => {
    fetchPaymentHistory();
  }, [pagination.page, filters]);

    // ✅ ADD YOUR NEW useEffect HERE:
  useEffect(() => {
    fetchAllCompletedPayments();
  }, []);

  const fetchPaymentHistory = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getPaymentHistory(pagination.page, pagination.limit, filters);
      setPayments(response.payments || []);
      setTotalRevenue(response.totalRevenue || 0);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0
      }));
      setError(null);
    } catch (err) {
      console.error('[PaymentsPage] Error fetching payments:', err);
      setError(err.response?.data?.message || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

   const fetchAllCompletedPayments = async () => {
  try {
    // First, get the total count of completed payments
    const firstResponse = await adminAPI.getPaymentHistory(1, 100, {
      status: 'COMPLETED',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    
    const totalCompleted = firstResponse.pagination?.total || 0;
    const totalPages = Math.ceil(totalCompleted / 100);
    
    console.log(`📊 Total completed payments: ${totalCompleted}, Pages: ${totalPages}`);
    
    // Fetch all pages
    let allPayments = [...(firstResponse.payments || [])];
    
    // Fetch remaining pages if needed
    for (let page = 2; page <= totalPages; page++) {
      const response = await adminAPI.getPaymentHistory(page, 100, {
        status: 'COMPLETED',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      allPayments = [...allPayments, ...(response.payments || [])];
      console.log(`📦 Fetched page ${page}/${totalPages}`);
    }
    
    setAllCompletedPayments(allPayments);
    console.log(`✅ Pie chart data loaded: ${allPayments.length} payments`);
  } catch (err) {
    console.error('[PaymentsPage] Error fetching all completed payments:', err);
  }
};

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'DEALER': return 'bg-purple-100 text-purple-800';
      case 'REAL_ESTATE_AGENT': return 'bg-blue-100 text-blue-800';
      case 'USER': return 'bg-gray-100 text-gray-800';
      case 'ADMIN': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'DEALER': return 'Dealer';
      case 'REAL_ESTATE_AGENT': return 'Agent';
      case 'USER': return 'User';
      case 'ADMIN': return 'Admin';
      default: return role;
    }
  };

  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">Payments & Monetization</h1>

      {/* TOTAL REVENUE CARD - Compact Version */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Revenue (Completed)</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">KES {totalRevenue.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">From {pagination.total} total transactions</p>
        </CardContent>
      </Card>

      {/* ⭐ ADD THIS - Income Pie Chart */}
      <IncomePieChart payments={allCompletedPayments} />

     



        {/* Payment History Card - ENHANCED WITH ALL FEATURES */}
        <Card className="md:col-span-2 lg:col-span-3">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" /> Payment History
            </CardTitle>
            <CardDescription>Click button below to view all transactions and payments.</CardDescription>
          </CardHeader>
          <CardContent>
            <button
              onClick={() => setIsTableVisible(!isTableVisible)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors mb-6"
            >
              <History className="w-4 h-4" />
              {isTableVisible ? 'Hide Payment History' : 'Show Payment History'}
            </button>
          
          {isTableVisible && (
            <>


              {/* FILTERS ROW */}
              <div className="flex gap-4 mb-6 flex-wrap">
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="PENDING">Pending</option>
                  <option value="FAILED">Failed</option>
                </select>

                <select
                  value={filters.plan}
                  onChange={(e) => handleFilterChange('plan', e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">All Plans</option>
                  <option value="BRONZE">Bronze</option>
                  <option value="SILVER">Silver</option>
                  <option value="GOLD">Gold</option>
                  <option value="PLATINUM">Platinum</option>
                  <option value="FEATURED_7D">Featured 7D</option>
                  <option value="FEATURED_14D">Featured 14D</option>
                  <option value="FEATURED_30D">Featured 30D</option>
                </select>

                <select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">All User Types</option>
                  <option value="USER">User</option>
                  <option value="DEALER">Dealer</option>
                  <option value="REAL_ESTATE_AGENT">Real Estate Agent</option>
                </select>

                <button
                  onClick={() => {
                    setFilters({
                      status: '',
                      plan: '',
                      role: '',
                      sortBy: 'createdAt',
                      sortOrder: 'desc'
                    });
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                >
                  Clear Filters
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-destructive mb-2">{error}</p>
                  <button onClick={fetchPaymentHistory} className="text-primary underline text-sm">
                    Retry
                  </button>
                </div>
              ) : payments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No payment history found.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b">
                        <tr className="text-left">
                          <th 
                            className="pb-2 font-medium cursor-pointer hover:bg-muted/50 px-2"
                            onClick={() => handleSort('createdAt')}
                          >
                            <div className="flex items-center gap-1">
                              Date <ArrowUpDown className="w-3 h-3" />
                            </div>
                          </th>
                          <th className="pb-2 font-medium px-2">User</th>
                          <th className="pb-2 font-medium px-2">Role</th>
                          <th 
                            className="pb-2 font-medium cursor-pointer hover:bg-muted/50 px-2"
                            onClick={() => handleSort('amount')}
                          >
                            <div className="flex items-center gap-1">
                              Amount <ArrowUpDown className="w-3 h-3" />
                            </div>
                          </th>
                          <th 
                            className="pb-2 font-medium cursor-pointer hover:bg-muted/50 px-2"
                            onClick={() => handleSort('plan')}
                          >
                            <div className="flex items-center gap-1">
                              Plan <ArrowUpDown className="w-3 h-3" />
                            </div>
                          </th>
                          <th className="pb-2 font-medium px-2">Status</th>
                          <th className="pb-2 font-medium px-2">M-Pesa Receipt</th>
                          <th className="pb-2 font-medium px-2">Listing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment) => (
                          <tr key={payment.id} className="border-b hover:bg-muted/50">
                            <td className="py-3 px-2">
                              {format(new Date(payment.createdAt), 'MMM d, yyyy HH:mm')}
                            </td>
                            <td className="py-3 px-2">
                              <div>
                                <p className="font-medium">{payment.user?.fullName || 'N/A'}</p>
                                <p className="text-xs text-muted-foreground">{payment.user?.email || 'N/A'}</p>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 rounded text-xs ${getRoleColor(payment.user?.role)}`}>
                                {getRoleLabel(payment.user?.role)}
                              </span>
                            </td>
                            <td className="py-3 px-2 font-medium">
                              KES {payment.amount.toLocaleString()}
                            </td>
                            <td className="py-3 px-2">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                {payment.plan}
                              </span>
                            </td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 rounded text-xs ${getStatusColor(payment.status)}`}>
                                {payment.status}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-xs text-muted-foreground">
                              {payment.mpesaReceiptNumber || '-'}
                            </td>
                            <td className="py-3 px-2 text-xs">
                              {payment.listing ? (
                                <div>
                                  <p className="font-medium">{payment.listing.title}</p>
                                  <p className="text-muted-foreground">{payment.listing.status}</p>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* SUMMARY TOTALS */}
                  {payments.length > 0 && (
                    <div className="mt-6 p-4 bg-muted/30 rounded-lg border">
                      <h3 className="font-semibold mb-3">Summary for Current View:</h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="flex flex-col">
                          <span className="text-muted-foreground mb-1">Completed</span>
                          <span className="text-lg font-bold text-green-600">
                            KES {payments
                              .filter(p => p.status === 'COMPLETED')
                              .reduce((sum, p) => sum + Number(p.amount), 0)
                              .toLocaleString()}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {payments.filter(p => p.status === 'COMPLETED').length} transactions
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-muted-foreground mb-1">Pending</span>
                          <span className="text-lg font-bold text-yellow-600">
                            KES {payments
                              .filter(p => p.status === 'PENDING')
                              .reduce((sum, p) => sum + Number(p.amount), 0)
                              .toLocaleString()}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {payments.filter(p => p.status === 'PENDING').length} transactions
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-muted-foreground mb-1">Failed</span>
                          <span className="text-lg font-bold text-red-600">
                            KES {payments
                              .filter(p => p.status === 'FAILED')
                              .reduce((sum, p) => sum + Number(p.amount), 0)
                              .toLocaleString()}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {payments.filter(p => p.status === 'FAILED').length} transactions
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PAGINATION */}
                  <div className="flex items-center justify-between mt-6">
                    <p className="text-sm text-muted-foreground">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} payments
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="px-3 py-1 border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm">
                        Page {pagination.page} of {pagination.pages}
                      </span>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.pages}
                        className="px-3 py-1 border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          </CardContent>
        </Card>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Listing Fees Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5" /> Listing Fees
            </CardTitle>
            <CardDescription>Manage fees for standard, premium, and featured listings.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Listing fee management UI will be here.</p>
          </CardContent>
        </Card>


        {/* Payment Integrations Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> Payment Integrations
            </CardTitle>
            <CardDescription>Configure M-Pesa, card payments, and PayPal.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Integration settings will be here.</p>
          </CardContent>
        </Card>

        {/* Subscriptions Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" /> Subscriptions
            </CardTitle>
            <CardDescription>Manage monthly/yearly plans for agencies or agents.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Subscription plan management UI will be here.</p>
          </CardContent>
        </Card>

        {/* Listing Tiers Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <List className="w-5 h-5" /> Listing Tiers
            </CardTitle>
            <CardDescription>Track paid vs. free listings.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">A report on listing tiers will be here.</p>
          </CardContent>
        </Card>

        {/* Revenue Reports Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> Revenue Reports
            </CardTitle>
            <CardDescription>View detailed revenue reports and analytics.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Revenue reports will be displayed here.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}